import TypographyParagraph from '../typography/TypographyParagraph'
import TypographyLabel from '../typography/TypographyLabel'
import {
	getCollectionProfileURL,
	mapChainSlugToIcon,
	truncateStringInMiddle,
	variableTruncateStringInMiddle,
} from '#app/utils/misc'

import InternalLink from '../common/internalLink'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { Theme } from '#app/utils/providers/themeProvider'

const Collection = ({
	metadata,
	asCountersign = false,
	theme = 'light',
}: {
	metadata: any
	asCountersign?: boolean
	theme?: Theme
}) => {
	return (
		<div className="flex flex-col">
			{metadata.properties.description && (
				<div
					className={`${asCountersign ? 'flex-row items-center border-b border-textInactiveLight px-4 py-2 dark:border-textInactiveDark' : 'mb-4'}`}
				>
					<TypographyParagraph
						size="medium"
						isBold={false}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{metadata.properties.description}
					</TypographyParagraph>
				</div>
			)}
			<div
				className={`flex h-full w-full flex-col rounded bg-uiTertiaryLight dark:bg-uiTertiaryDark ${asCountersign ? '' : 'border border-textInactiveLight dark:border-textInactiveDark'}`}
			>
				<div
					id="collectionName"
					className="flex flex-row justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
				>
					<TypographyLabel
						size="medium"
						isBold={true}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-words"
					>
						<InternalLink to={getCollectionProfileURL(metadata)}>
							{metadata.properties.name}
						</InternalLink>
					</TypographyLabel>
				</div>
				<div className="flex w-full flex-col">
					<div className="flex flex-col sm:flex-row">
						<div
							id="creationDate"
							className="flex flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r md:flex-row"
						>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Creation Date:
							</TypographyParagraph>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-1"
							>
								{metadata.properties.date
									? metadata.properties.date
									: metadata.createdAt}
							</TypographyParagraph>
						</div>
						<div
							id="creatorWallet"
							className="flex flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 md:flex-row"
						>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Creator Wallet:
							</TypographyParagraph>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-1"
							>
								{truncateStringInMiddle(metadata.address)}
							</TypographyParagraph>
						</div>
					</div>
					{metadata.collectionEntries &&
						metadata.collectionEntries.map((entry, index) => (
							<div
								className="flex flex-col sm:flex-row"
								key={`${metadata.createdAt}-collection-entry-${index}-post`}
							>
								<div
									id="contract"
									className="flex flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r md:flex-row"
								>
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
									>
										Contract:
									</TypographyParagraph>
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all truncate whitespace-no-wrap ml-1"
									>
										{entry.contract && entry.contract.length > 0
											? truncateStringInMiddle(entry.contract)
											: 'N/A'}
									</TypographyParagraph>
								</div>
								<div
									id="blockchain"
									className="flex flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 md:flex-row"
								>
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
									>
										Tokens:
									</TypographyParagraph>
									<div className="flex">
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all truncate whitespace-no-wrap ml-1"
										>
											{entry.hasOwnProperty('isAwaiting') &&
											entry.isAwaiting == true ? (
												<SkeletonTheme
													baseColor={
														theme === 'light' || theme === undefined
															? '#f5f5f5'
															: '#383838'
													}
													highlightColor={
														theme === 'light' || theme === undefined
															? '#f8f8f8'
															: '#212121'
													}
												>
													<Skeleton circle={true} className="h-6 w-6" />
												</SkeletonTheme>
											) : (
												`${entry.tokenCount}`
											)}
										</TypographyParagraph>
										<div className="ml-1 flex h-4 w-4 text-textPrimaryLight dark:text-textPrimaryDark">
											{mapChainSlugToIcon(entry.chain)}
										</div>
									</div>
								</div>
							</div>
						))}
				</div>
				<div className="flex flex-col sm:flex-row">
					{metadata.properties.url && (
						<div
							id="externalLink"
							className="flex w-full flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark md:flex-row"
						>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark text-nowrap"
							>
								External Link:
							</TypographyParagraph>
							<a
								href={metadata.properties.url}
								target="_blank"
								rel="noreferrer"
							>
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all truncate whitespace-normal ml-1"
								>
									{variableTruncateStringInMiddle(metadata.properties.url, 58)}
								</TypographyParagraph>
							</a>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default Collection
